import React, { useState, useEffect } from 'react';
import { colors } from '../utils/colors';

export type CalendarMark = 'present' | 'late' | 'absent';

interface Props {
  visible: boolean;
  onClose: () => void;
  selectedDate: string;                          // ISO YYYY-MM-DD
  onSelectDate: (dateISO: string) => void;
  markedDates?: Record<string, CalendarMark>;    // ISO → status
  maxDate?: string;                              // disable future dates (default: today)
}

const DAYS    = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTHS  = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

const DOT: Record<CalendarMark, string> = {
  present: '#22a06b',
  late:    '#eab308',
  absent:  '#e04444',
};

const todayISO = () => new Date().toISOString().split('T')[0];

function daysInMonth(y: number, m: number) { return new Date(y, m + 1, 0).getDate(); }
function firstWeekday(y: number, m: number) { return new Date(y, m, 1).getDay(); }

const MonthCalendar: React.FC<Props> = ({
  visible, onClose, selectedDate, onSelectDate, markedDates = {}, maxDate,
}) => {
  const ceiling = maxDate ?? todayISO();
  const today   = todayISO();

  const [year,  setYear]  = useState(() => parseInt(selectedDate.slice(0, 4)));
  const [month, setMonth] = useState(() => parseInt(selectedDate.slice(5, 7)) - 1);

  // Sync view to selectedDate whenever sheet opens
  useEffect(() => {
    if (visible) {
      setYear(parseInt(selectedDate.slice(0, 4)));
      setMonth(parseInt(selectedDate.slice(5, 7)) - 1);
    }
  }, [visible, selectedDate]);

  const prevMonth = () => {
    if (month === 0) { setYear(y => y - 1); setMonth(11); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    const firstOfNext = `${month === 11 ? year + 1 : year}-${String(month === 11 ? 1 : month + 2).padStart(2,'0')}-01`;
    if (firstOfNext > ceiling) return;          // don't go past max
    if (month === 11) { setYear(y => y + 1); setMonth(0); }
    else setMonth(m => m + 1);
  };

  const total   = daysInMonth(year, month);
  const startDow = firstWeekday(year, month);

  const cells: (number | null)[] = [
    ...Array<null>(startDow).fill(null),
    ...Array.from({ length: total }, (_, i) => i + 1),
  ];
  while (cells.length % 7 !== 0) cells.push(null);

  const isoOf = (day: number) =>
    `${year}-${String(month + 1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;

  const nextMonthStart = `${month === 11 ? year + 1 : year}-${String(month === 11 ? 1 : month + 2).padStart(2,'0')}-01`;
  const nextDisabled   = nextMonthStart > ceiling;

  if (!visible) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(3px)',
          WebkitBackdropFilter: 'blur(3px)',
          zIndex: 300,
        }}
      />

      {/* Sheet */}
      <div style={{
        position: 'fixed', left: 0, right: 0, bottom: 0,
        background: '#fff', borderRadius: '22px 22px 0 0',
        zIndex: 301,
        padding: '0 20px 40px',
        animation: 'sheetSlideUp 0.3s cubic-bezier(.34,1.06,.64,1) both',
        fontFamily: "'Inter', sans-serif",
      }}>
        {/* Drag handle */}
        <div style={{
          width: 40, height: 4, borderRadius: 2,
          background: colors.border,
          margin: '14px auto 22px',
        }} />

        {/* Month nav */}
        <div style={{
          display: 'flex', alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 18,
        }}>
          <button onClick={prevMonth} style={navBtnStyle}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
              stroke={colors.primary} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6"/>
            </svg>
          </button>

          <div style={{ fontSize: 17, fontWeight: 800, color: colors.text }}>
            {MONTHS[month]} {year}
          </div>

          <button
            onClick={nextMonth}
            disabled={nextDisabled}
            style={{ ...navBtnStyle, opacity: nextDisabled ? 0.3 : 1 }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
              stroke={colors.primary} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6"/>
            </svg>
          </button>
        </div>

        {/* Weekday headers */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', marginBottom: 6 }}>
          {DAYS.map((d, i) => (
            <div key={d} style={{
              textAlign: 'center', fontSize: 12, fontWeight: 700,
              // Sunday (index 0) shown in red as a visual indicator — purely decorative
              color: i === 0 ? '#e04444' : colors.textLight,
              padding: '4px 0',
            }}>
              {d}
            </div>
          ))}
        </div>

        {/* Day cells */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)' }}>
          {cells.map((day, idx) => {
            if (!day) return <div key={`e-${idx}`} />;

            const iso        = isoOf(day);
            const isToday    = iso === today;
            const isSelected = iso === selectedDate;
            const isFuture   = iso > ceiling;
            const isSunday   = (startDow + (day - 1)) % 7 === 0;
            const dot        = markedDates[iso];

            // Determine number colour: selected → white, today → primary,
            // sunday (unselected, not today) → red tint, future → muted, else → default text
            const numberColor = isSelected
              ? '#fff'
              : isToday
              ? colors.primary
              : isSunday
              ? '#e04444'
              : colors.text;

            return (
              <button
                key={iso}
                onClick={() => !isFuture && !isSunday && onSelectDate(iso)}
                style={{
                  display: 'flex', flexDirection: 'column',
                  alignItems: 'center', padding: '5px 0',
                  // Reset all inherited browser styles on <button>
                  background: 'none', border: 'none', outline: 'none',
                  WebkitAppearance: 'none',
                  cursor: isFuture || isSunday ? 'default' : 'pointer',
                  opacity: isFuture ? 0.28 : isSunday ? 0.4 : 1,
                }}
              >
                {/* Day circle */}
                <div style={{
                  width: 36, height: 36, borderRadius: '50%',
                  background: isSelected
                    ? colors.primaryDark
                    : isToday
                    ? colors.primaryBg
                    : 'transparent',
                  border: isToday && !isSelected
                    ? `1.5px solid ${colors.primary}`
                    : 'none',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'background 0.15s',
                }}>
                  <span style={{
                    fontSize: 14,
                    fontWeight: isToday || isSelected ? 700 : 400,
                    // Explicitly set color — never inherit from browser/global CSS
                    color: numberColor,
                  }}>
                    {day}
                  </span>
                </div>

                {/* Status dot */}
                <div style={{
                  width: 5, height: 5, borderRadius: '50%', marginTop: 3,
                  background: dot ? DOT[dot] : 'transparent',
                }} />
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
};

const navBtnStyle: React.CSSProperties = {
  background: '#F5EEFA', border: 'none', borderRadius: 10,
  width: 36, height: 36, cursor: 'pointer',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};

export default MonthCalendar;